import { supabase } from '../../lib/supabase';
import type {
  PostmarkTemplate,
  PostmarkTemplateDetail,
  PostmarkTemplateInput,
  PostmarkTemplatePreviewResult,
} from './types';

/**
 * Thin wrappers around `supabase.functions.invoke('postmark-templates', …)`.
 * All write actions throw on any non-2xx response with the Postmark message
 * surfaced.
 */

/**
 * `supabase.functions.invoke` does NOT put the response body into `data` when
 * the status is non-2xx. On error it returns a `FunctionsHttpError` whose
 * `context` is the raw `Response`. To surface our edge fn's `{ error: "..." }`
 * payload we have to read that body ourselves.
 */
async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('postmark-templates', {
    body,
  });
  if (error) {
    let detail: string | null = null;
    const ctx = (error as { context?: unknown }).context;
    if (ctx && typeof (ctx as Response).json === 'function') {
      try {
        const parsed = await (ctx as Response).clone().json();
        if (parsed && typeof parsed.error === 'string') detail = parsed.error;
      } catch {
        try {
          detail = await (ctx as Response).clone().text();
        } catch {
          /* ignore */
        }
      }
    }
    if (!detail) {
      detail = (data as { error?: string } | null)?.error ?? null;
    }
    throw new Error(detail || error.message);
  }
  return data as T;
}

export async function listPostmarkTemplates(opts?: {
  activeOnly?: boolean;
  includeLayouts?: boolean;
}): Promise<PostmarkTemplate[]> {
  const data = await invoke<{ templates?: PostmarkTemplate[] }>({
    action: 'list',
    activeOnly: opts?.activeOnly === true,
    includeLayouts: opts?.includeLayouts === true,
  });
  return data.templates ?? [];
}

export async function getPostmarkTemplate(
  id: number | string,
): Promise<PostmarkTemplateDetail> {
  const data = await invoke<{ template: PostmarkTemplateDetail }>({
    action: 'get',
    id,
  });
  return data.template;
}

export async function createPostmarkTemplate(
  template: PostmarkTemplateInput,
): Promise<PostmarkTemplateDetail> {
  const data = await invoke<{ template: PostmarkTemplateDetail }>({
    action: 'create',
    template,
  });
  return data.template;
}

export async function updatePostmarkTemplate(
  id: number | string,
  template: PostmarkTemplateInput,
): Promise<PostmarkTemplateDetail> {
  const data = await invoke<{ template: PostmarkTemplateDetail }>({
    action: 'update',
    id,
    template,
  });
  return data.template;
}

export async function deletePostmarkTemplate(id: number | string): Promise<void> {
  await invoke<{ ok: true }>({ action: 'delete', id });
}

export async function previewPostmarkTemplate(
  template: PostmarkTemplateInput,
): Promise<PostmarkTemplatePreviewResult> {
  return invoke<PostmarkTemplatePreviewResult>({
    action: 'preview',
    template,
  });
}
